/**
 * Микрофон для звонка (изменение v1.14).
 *
 * Разрешение запрашивается только действием пользователя — «Позвонить» или «Ответить»
 * (ПС §18), и до того, как звонок создан на сервере: отказ не должен оставлять у собеседника
 * звонок, на который некому ответить. Эхо- и шумоподавление, АРУ и моно — голосовой режим.
 */

export type MicrophoneFailure = 'denied' | 'not_found' | 'busy' | 'failed'

export class MicrophoneError extends Error {
  constructor(readonly reason: MicrophoneFailure) {
    super(`microphone_${reason}`)
    this.name = 'MicrophoneError'
  }
}

export async function openMicrophone(): Promise<MediaStream> {
  const devices = globalThis.navigator?.mediaDevices
  if (!devices?.getUserMedia) throw new MicrophoneError('failed')
  try {
    return await devices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      video: false,
    })
  } catch (error) {
    const name = error instanceof DOMException ? error.name : ''
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new MicrophoneError('denied')
    if (name === 'NotFoundError' || name === 'OverconstrainedError') throw new MicrophoneError('not_found')
    if (name === 'NotReadableError' || name === 'AbortError') throw new MicrophoneError('busy')
    throw new MicrophoneError('failed')
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  for (const track of stream?.getTracks() ?? []) track.stop()
}

export function microphoneMessage(reason: MicrophoneFailure): string {
  switch (reason) {
    case 'denied':
      return 'Нет доступа к микрофону. Разрешите его для «Движа» в настройках браузера и позвоните снова.'
    case 'not_found':
      return 'Микрофон не найден. Подключите его и попробуйте снова.'
    case 'busy':
      return 'Микрофон занят — завершите другой звонок или запись и попробуйте снова.'
    case 'failed':
      return 'Не удалось включить микрофон.'
  }
}
